import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { register } from '../api/client';
import { Wrench, CheckCircle } from 'lucide-react';

const FEATURES = ['Unlimited work orders', 'GPS technician tracking', 'AI repair assistant', 'PDF invoicing', 'Parts inventory', 'Multi-user access'];

export default function Register() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    company: { name: '', email: '', phone: '', address: '' },
    admin: { full_name: '', email: '', password: '', role: 'admin' },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await register(form);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const inp = (label, key, section, type = 'text', required = false) => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}{required && ' *'}</label>
      <input type={type} required={required} value={form[section][key]}
        onChange={e => setForm({ ...form, [section]: { ...form[section], [key]: e.target.value } })}
        style={{ width: '100%', padding: '9px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13 }} />
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#F9FAFB', display: 'flex' }}>
      {/* Left panel */}
      <div style={{ width: 380, background: 'linear-gradient(160deg, #1E3A8A 0%, #2563EB 100%)', padding: '60px 40px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 40 }}>
          <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 10, padding: 8 }}>
            <Wrench size={24} color="#fff" />
          </div>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 18 }}>Service Tech Manager</div>
        </div>
        <h2 style={{ color: '#fff', fontSize: 26, fontWeight: 700, lineHeight: 1.3, marginBottom: 12 }}>Start your 14-day free trial</h2>
        <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, marginBottom: 32 }}>Then starting at $29/month. Cancel anytime.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {FEATURES.map(f => (
            <div key={f} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <CheckCircle size={16} color="#34D399" />
              <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: 14 }}>{f}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
        <div style={{ width: '100%', maxWidth: 460 }}>

          {success ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                <CheckCircle size={32} color="#10B981" />
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 10 }}>Registration Successful!</h2>
              <p style={{ color: '#6B7280', fontSize: 14, marginBottom: 28 }}>
                Your account has been created. Please log in to get started.
              </p>
              <button
                onClick={() => navigate('/login')}
                style={{ padding: '12px 32px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 15 }}
              >
                Go to Login
              </button>
            </div>
          ) : (
          <>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', marginBottom: 6 }}>Create your account</h1>
          <p style={{ color: '#6B7280', marginBottom: 28, fontSize: 14 }}>
            Already have an account? <Link to="/login" style={{ color: '#2563EB', fontWeight: 600, textDecoration: 'none' }}>Sign in</Link>
          </p>

          {/* Steps */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 28 }}>
            {['Company Info', 'Admin Account'].map((label, i) => (
              <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{
                  height: 4, borderRadius: 2, marginBottom: 6,
                  background: step > i ? '#2563EB' : step === i + 1 ? '#93C5FD' : '#E5E7EB',
                }} />
                <span style={{ fontSize: 11, color: step === i + 1 ? '#2563EB' : '#9CA3AF', fontWeight: step === i + 1 ? 600 : 400 }}>{label}</span>
              </div>
            ))}
          </div>

          {error && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
              {error}
            </div>
          )}

          <form onSubmit={step === 1 ? (e) => { e.preventDefault(); setStep(2); } : handleSubmit}>
            {step === 1 ? (
              <>
                {inp('Company Name', 'name', 'company', 'text', true)}
                {inp('Company Email', 'email', 'company', 'email', true)}
                {inp('Phone', 'phone', 'company')}
                {inp('Address', 'address', 'company')}
              </>
            ) : (
              <>
                {inp('Your Full Name', 'full_name', 'admin', 'text', true)}
                {inp('Your Email', 'email', 'admin', 'email', true)}
                {inp('Password (min 8 chars)', 'password', 'admin', 'password', true)}
              </>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              {step > 1 && (
                <button type="button" onClick={() => setStep(1)}
                  style={{ flex: 1, padding: 12, border: '1px solid #D1D5DB', borderRadius: 8, background: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                  Back
                </button>
              )}
              <button type="submit" disabled={loading} style={{
                flex: 1, padding: 12, background: '#2563EB', color: '#fff',
                border: 'none', borderRadius: 8, cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: 600, fontSize: 15, opacity: loading ? 0.7 : 1,
              }}>
                {step === 1 ? 'Continue →' : loading ? 'Creating...' : 'Start Free Trial'}
              </button>
            </div>
          </form>

          <p style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'center', marginTop: 20 }}>
            By creating an account you agree to our Terms of Service and Privacy Policy.
          </p>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
